import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { t } from '../i18n';
import { Button, Input, Field, Alert, Spinner } from '../components/ui';

/**
 * Where the reset link lands, to choose a new password.
 *
 * Clicking the emailed link signs the person in with a recovery session, which
 * supabase-js picks up from the URL (detectSessionInUrl). So this screen is
 * reachable while "authenticated" and must not be behind the redirect that
 * sends signed-in users home — otherwise anyone with a working account would
 * be bounced away from the very page they were sent to.
 */

const ResetPasswordView = () => {
  const [ready, setReady] = useState(false);
  const [validSession, setValidSession] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // The client needs a moment to exchange the tokens in the URL.
    const check = async () => {
      const { data } = await supabase.auth.getSession();
      setValidSession(Boolean(data?.session));
      setReady(true);
    };
    const timer = setTimeout(check, 700);
    return () => clearTimeout(timer);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    if (password.length < 8) { setError(t('register.passwordTooShort')); return; }
    if (password !== confirmPassword) { setError(t('register.passwordMismatch')); return; }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateError) { setError(updateError.message); return; }
    setDone(true);
  };

  if (!ready) return <Spinner />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-indigo-900">{t('common.appName')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('reset.title')}</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          {done ? (
            <div className="text-center">
              <Alert tone="success">{t('reset.doneBody')}</Alert>
              <Button className="mt-6 w-full" onClick={() => navigate('/ingresar')}>
                {t('auth.login')}
              </Button>
            </div>
          ) : !validSession ? (
            // Recovery links are single use and time limited.
            <div className="text-center">
              <Alert tone="danger" title={t('reset.expiredTitle')}>{t('reset.expiredBody')}</Alert>
              <Button className="mt-6 w-full" onClick={() => navigate('/recuperar')}>
                {t('reset.requestAnother')}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <Alert tone="danger">{error}</Alert>}

              <Field label={t('reset.newPassword')} required hint={t('register.passwordHint')}>
                <Input
                  type="password" value={password} autoComplete="new-password" required disabled={loading}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              <Field label={t('register.confirmPassword')} required>
                <Input
                  type="password" value={confirmPassword} autoComplete="new-password" required disabled={loading}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </Field>

              <Button type="submit" loading={loading} className="w-full">
                {t('reset.submit')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordView;
